import { PropsWithChildren } from 'react';
import { Modal, StyleSheet, View, Text } from 'react-native';
import GenericCancel from '../molecules/GenericCancel';
import GenericButton from '../molecules/GenericButton';
import { getResponsiveContainerWidth, getResponsiveFontSize } from '../../helpers/utils';

export interface ModalActions {
  isVisible: boolean; // Boolean to control modal visibility
  onClose: () => void; // Function to handle modal close
  title: string; // Modal title
  children?: React.ReactNode; // Content inside the modal (passed as children) - made optional
  onConfirm: () => void; // Function to handle modal confirm
  isLoading?: boolean; // Boolean to control loading state
  containerStyle?: object; // Custom style for modal container
  overlayStyle?: object; // Custom style for modal overlay
  acceptText?: string; // Text for the accept button
  changeCredentialType?: boolean; //
  setIsAnonCred?: (value: boolean) => void;
  isAnonCred?: boolean;
  isDisabled?: boolean;
}

/**
 * BlankModal component
 *
 * @param {*} {
 *   isVisible,
 *   onClose,
 *   title,
 *   children,
 *   onConfirm,
 *   containerStyle = {},
 *   overlayStyle = {},
 *   acceptText,
 *   isLoading = false
 * }
 * @return {*}
 */
const BlankModal: React.FC<PropsWithChildren<ModalActions>> = ({
  isVisible,
  onClose,
  title,
  children,
  onConfirm,
  containerStyle = {},
  overlayStyle = {},
  acceptText,
  isLoading = false,
  isDisabled = false
}) => {
  return (
    <Modal transparent visible={isVisible} animationType="fade" onRequestClose={onClose}>
      <View style={[styles.modalOverlay, overlayStyle]}>
        <View style={[styles.modalContainer, containerStyle]}>
          {/* Modal Title */}
          {title && (
            <View style={styles.titleContainer}>
              <Text style={styles.title}>{title}</Text>
            </View>
          )}

          {/* Modal Content */}
          {children && <View style={styles.content}>{children}</View>}

          {/* Modal Buttons */}
          <View style={styles.buttonContainer}>
            <GenericCancel
              onPress={() => onClose()}
              buttonStyle={{ marginRight: 8 }}
              //isDisabled={isLoading}
            />
            <GenericButton
              title={acceptText || 'Invite'}
              onPress={() => onConfirm()}
              isPrimary
              isDisabled={isLoading || isDisabled}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20 // Add padding to prevent modal from touching screen edges
  },
  modalContainer: {
    width: '100%',
    maxWidth: getResponsiveContainerWidth(), // Responsive width with max constraint
    minWidth: getResponsiveContainerWidth(), // Minimum width for small screens
    padding: 20,
    borderRadius: 10,
    backgroundColor: '#fff',
    boxShadow: '0 4px 4px rgba(0, 0, 0, 0.2)',
    zIndex: 1000,
    // Use flex properties for better layout control
    alignSelf: 'center',
    overflow: 'hidden' // Prevent content from overflowing
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexShrink: 0 // Prevent title from shrinking
  },
  title: {
    fontSize: getResponsiveFontSize(14, 18, 24), // Responsive font size
    fontWeight: 'bold',
    marginBottom: 20,
    flexShrink: 0 // Prevent title from shrinking
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    justifyContent: 'space-between',
    flexWrap: 'wrap' // Allow wrapping on small screens
  },
  switchLabel: {
    fontSize: getResponsiveFontSize(12, 14, 16),
    marginRight: 10
  },
  switchValue: {
    fontSize: getResponsiveFontSize(12, 14, 16),
    fontWeight: 'bold',
    marginRight: 10
  },
  content: {
    marginBottom: 20,
    flex: 1, // Allow content to grow and shrink
    minHeight: 0 // Allow content to shrink below its natural size
  },
  buttonContainer: {
    flexDirection: getResponsiveContainerWidth() < 300 ? 'column' : 'row', // Stack buttons on very small screens
    justifyContent: 'flex-end',
    flexShrink: 0, // Prevent button container from shrinking
    gap: getResponsiveContainerWidth() < 300 ? 8 : 0 // Add gap between stacked buttons
  },
  button: {
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderRadius: 5,
    marginLeft: getResponsiveContainerWidth() < 300 ? 0 : 10, // Remove left margin when stacked
    marginTop: getResponsiveContainerWidth() < 300 ? 8 : 0, // Add top margin when stacked
    minWidth: getResponsiveContainerWidth() < 300 ? '100%' : 'auto' // Full width when stacked
  },
  buttonText: {
    color: '#fff',
    fontWeight: 'bold'
  }
});

export default BlankModal;
